// src/pages/Register.jsx
import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { createUserWithEmailAndPassword, updateProfile } from "firebase/auth";
import { auth } from "../lib/firebase";
import { createDesigner, createContractor, getInviteLinkPreview, claimDesignerInvite } from "../lib/db";
import { Btn, Input, Field } from "../components/UI";
import BrandLogo from "../components/BrandLogo";

export default function Register() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get("invite")?.trim() || "";
  const [invitePreview, setInvitePreview] = useState(null);

  const [form, setForm] = useState({
    accountKind: "designer",
    firstName: "", lastName: "", firm: "", email: "", phone: "", password: "", confirm: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!inviteToken) {
      setInvitePreview(null);
      return;
    }
    getInviteLinkPreview(inviteToken).then(setInvitePreview).catch(() => setInvitePreview(null));
  }, [inviteToken]);

  useEffect(() => {
    if (invitePreview) setForm((f) => ({ ...f, accountKind: "designer" }));
  }, [invitePreview]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm) {
      setError("Passwords do not match.");
      return;
    }
    if (form.password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    if (invitePreview && form.accountKind !== "designer") {
      setError("Designer invitations must register as a designer.");
      return;
    }
    const emailNorm = form.email.trim().toLowerCase();
    setError("");
    setLoading(true);
    try {
      const cred = await createUserWithEmailAndPassword(auth, emailNorm, form.password);
      await updateProfile(cred.user, { displayName: `${form.firstName} ${form.lastName}` });
      const payload = {
        name: `${form.firstName} ${form.lastName}`,
        email: emailNorm,
        firm: form.firm,
        phone: form.phone,
      };
      if (form.accountKind === "contractor") {
        await createContractor(cred.user.uid, { ...payload, commissionPending: true });
      } else {
        await createDesigner(cred.user.uid, { ...payload, commissionPending: true });
      }
      if (inviteToken) {
        await claimDesignerInvite(inviteToken, cred.user.uid, emailNorm, "designers");
      }
      navigate("/portal");
    } catch (err) {
      setError(err.code === "auth/email-already-in-use" ? "That email is already registered." : err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", minHeight: "100vh" }}>
      <div style={{ background: "var(--taupe)", display: "flex", flexDirection: "column", justifyContent: "center", padding: 60 }}>
        <BrandLogo variant="light" style={{ maxWidth: 280, marginBottom: 28 }} />
        <h2 style={{ fontFamily: "'Bodoni Moda', serif", fontSize: "clamp(34px, 4vw, 44px)", fontWeight: 400, fontStyle: "italic", color: "#FFFFFF", lineHeight: 1.15, marginBottom: 18, letterSpacing: "-0.02em" }}>Join the Program</h2>
        <p style={{ fontSize: 13, color: "rgba(255,255,255,0.58)", lineHeight: 1.8, maxWidth: 340 }}>
          Earn tiered commission on orders you refer to our Los Angeles showroom. Annual tier progress resets each year from your join anniversary (365-day program year).
        </p>
      </div>

      <div style={{ background: "#FFFFFF", display: "flex", alignItems: "center", justifyContent: "center", padding: 60 }}>
        <div style={{ width: "100%", maxWidth: 400 }}>
          <div style={{ marginBottom: 12 }}>
            <BrandLogo variant="dark" style={{ maxWidth: 180 }} />
          </div>
          <h3 style={{ fontFamily: "'Bodoni Moda', serif", fontSize: 26, fontWeight: 400, marginBottom: 4, color: "var(--charcoal)" }}>Create your account</h3>
          <p style={{ fontSize: 12, color: "var(--mid)", marginBottom: 28 }}>
            Already a member?{" "}
            <span onClick={() => navigate("/login")} style={{ color: "var(--gold)", cursor: "pointer" }}>
              Sign in
            </span>
          </p>

          {invitePreview && (
            <div style={{ background: "var(--gold-pale)", border: "1px solid var(--border)", padding: "12px 14px", borderRadius: 2, fontSize: 12, color: "#5D544B", marginBottom: 16, lineHeight: 1.6 }}>
              You’re invited by <strong>{invitePreview.referrerName || "a showroom designer"}</strong>. Use email <strong>{invitePreview.inviteeEmail}</strong> — it must match this invitation exactly.
            </div>
          )}

          {error && <div style={{ background: "var(--red-light)", color: "var(--red)", padding: "10px 14px", borderRadius: 2, fontSize: 12, marginBottom: 16 }}>{error}</div>}

          <form onSubmit={handleSubmit}>
            <Field label="Account type">
              <div style={{ display: "flex", gap: 16, fontSize: 13 }}>
                <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: invitePreview ? "not-allowed" : "pointer", opacity: invitePreview ? 0.5 : 1 }}>
                  <input
                    type="radio"
                    name="kind"
                    checked={form.accountKind === "designer"}
                    disabled={!!invitePreview}
                    onChange={() => setForm((f) => ({ ...f, accountKind: "designer" }))}
                  />
                  Designer
                </label>
                <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: invitePreview ? "not-allowed" : "pointer", opacity: invitePreview ? 0.5 : 1 }}>
                  <input
                    type="radio"
                    name="kind"
                    checked={form.accountKind === "contractor"}
                    disabled={!!invitePreview}
                    onChange={() => setForm((f) => ({ ...f, accountKind: "contractor" }))}
                  />
                  Contractor
                </label>
              </div>
            </Field>
            <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
              <Field label="First Name" half>
                <Input value={form.firstName} onChange={set("firstName")} required />
              </Field>
              <Field label="Last Name" half>
                <Input value={form.lastName} onChange={set("lastName")} required />
              </Field>
            </div>
            <Field label="Firm / Studio">
              <Input value={form.firm} onChange={set("firm")} placeholder="Studio Name LLC" required />
            </Field>
            <Field label="Email">
              <Input type="email" value={form.email} onChange={set("email")} required />
            </Field>
            <Field label="Phone (optional)">
              <Input type="tel" value={form.phone} onChange={set("phone")} placeholder="(310) 555-0100" />
            </Field>
            <Field label="Password">
              <Input type="password" value={form.password} onChange={set("password")} required />
            </Field>
            <Field label="Confirm Password">
              <Input type="password" value={form.confirm} onChange={set("confirm")} required />
            </Field>
            <Btn type="submit" variant="gold" size="lg" fullWidth disabled={loading}>
              {loading ? "Creating account…" : "Create Account"}
            </Btn>
          </form>
        </div>
      </div>
    </div>
  );
}
